// Phase 3D / Iter84 — Snapshot Source Strip.
//
// Visual + apply helper that the five toolbox module scanner panels
// embed above their friendly chain editor. It reads the latest
// in-memory snapshot from `MarketSnapshotContext`, lets the operator
// pick a symbol from the snapshot, and on explicit click triggers
// `onApply(extracted)` so the host panel can populate its form
// state without any HTTP, without running the scanner, and without
// persisting anything.
//
// HARD CONSTRAINTS:
//  * No outbound HTTP. No `useEffect`. No `axios` import.
//  * No localStorage / sessionStorage / IndexedDB / cookies.
//  * Never runs the scanner. Never auto-applies. Operator must
//    explicitly click "Use Fresh Snapshot".
//  * Never invokes `/forge/candidates`, `/market-scanner/*`, any
//    scanner preview route, or any broker/account/journal endpoint.
//  * Never declares a winner / best / preferred strategy.
//  * Never fabricates chain rows — when puts/calls are missing it
//    surfaces explicit warnings instead.

import React, { useMemo, useState } from "react";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import { AlertTriangle, Wand2, Clock } from "lucide-react";

const STALE_SECONDS = 5 * 60;

function ageSeconds(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

function findRow(snapshot, symbol) {
  if (!snapshot || !Array.isArray(snapshot.rows)) return null;
  return snapshot.rows.find(r => r && r.symbol === symbol) || null;
}

function splitChain(row) {
  const chain = (row && Array.isArray(row.chain)) ? row.chain : [];
  return {
    puts: chain.filter(r => r && r.type === "put"),
    calls: chain.filter(r => r && r.type === "call"),
  };
}

// Module → extraction strategy.
//
// Each strategy returns a payload that the host's onApply consumes:
//   {
//     symbol, spot, ivr, expiration, dte,
//     chain_json:        string (BPCS/BCCS/CFM_CC_SHARES single chain),
//     put_chain_json:    string (Iron Condor),
//     call_chain_json:   string (Iron Condor),
//     short_calls_json:  string (CFM_CC_LEAPS),
//     leaps_calls_json:  string (CFM_CC_LEAPS; usually unavailable),
//     warnings:          string[],
//   }
//
// Every JSON string is built from the snapshot row's chain ONLY.
// Missing sides do not get synthetic rows; they get warnings.

function extractForModule(module, row) {
  const { puts, calls } = splitChain(row);
  const base = {
    symbol: row.symbol,
    spot: row.spot_price,
    ivr: row.ivr,
    expiration: row.chosen_expiration,
    dte: row.dte,
    warnings: [],
  };
  const toJson = arr => JSON.stringify(arr, null, 2);

  if (module === "BPCS") {
    return {
      ...base,
      chain_json: toJson(puts),
      warnings: puts.length === 0
        ? ["Snapshot has no puts for BPCS."]
        : [],
    };
  }
  if (module === "BCCS") {
    return {
      ...base,
      chain_json: toJson(calls),
      warnings: calls.length === 0
        ? ["Snapshot has no calls for BCCS."]
        : [],
    };
  }
  if (module === "IRON_CONDOR") {
    return {
      ...base,
      put_chain_json: toJson(puts),
      call_chain_json: toJson(calls),
      warnings: (puts.length === 0 || calls.length === 0)
        ? ["Iron Condor requires both puts and calls."]
        : [],
    };
  }
  if (module === "CFM_CC_SHARES") {
    return {
      ...base,
      chain_json: toJson(calls),
      warnings: [
        ...(calls.length === 0 ? ["Snapshot has no calls for CFM Shares."] : []),
        "Snapshot provides chain only. Inventory context still required.",
      ],
    };
  }
  if (module === "CFM_CC_LEAPS") {
    // Phase 3D does not auto-detect long-dated LEAPS. We fill
    // short_calls only, and warn that LEAPS context must be
    // entered manually.
    return {
      ...base,
      short_calls_json: toJson(calls),
      leaps_calls_json: "[]",
      warnings: [
        ...(calls.length === 0 ? ["Snapshot has no calls for CFM LEAPS."] : []),
        "Snapshot provides short-call chain only. LEAPS chain/context still required.",
      ],
    };
  }
  return { ...base, warnings: ["Unknown module"] };
}

export default function SnapshotSourceStrip({ module, onApply, testidPrefix }) {
  const { snapshot } = useMarketSnapshot();
  const [chosenSymbol, setChosenSymbol] = useState("");
  const [appliedFor, setAppliedFor] = useState(null);

  const symbols = useMemo(() => {
    if (!snapshot || !Array.isArray(snapshot.rows)) return [];
    return snapshot.rows
      .filter(r => r && r.symbol)
      .map(r => r.symbol);
  }, [snapshot]);

  if (!snapshot) {
    return null;
  }

  const selected = chosenSymbol || symbols[0] || "";
  const row = findRow(snapshot, selected);
  const ageS = ageSeconds(snapshot.as_of);
  const stale = ageS !== null && ageS > STALE_SECONDS;

  function apply() {
    if (!row || !onApply) return;
    const payload = extractForModule(module, row);
    onApply(payload);
    setAppliedFor(selected);
  }

  const tid = testidPrefix || "snapshot-source-strip";

  return (
    <div
      data-testid={tid}
      className="mb-3 rounded-sm border border-[#00FF88]/20 bg-[#00FF88]/5 p-2 text-[10px] font-mono space-y-1"
    >
      <div className="flex items-center gap-2 flex-wrap text-[#00FF88]">
        <Wand2 size={11} />
        <span className="uppercase tracking-[0.14em] font-bold">
          Use Fresh Snapshot
        </span>
        <span className="text-[#00FF88]/60">·</span>
        <span data-testid={`${tid}-snapshot-id`} className="text-[#00FF88]/70">
          {snapshot.snapshot_id?.slice(0, 8)}
        </span>
        <span className="text-[#00FF88]/60">·</span>
        <span data-testid={`${tid}-age`} className="text-[#00FF88]/70 inline-flex items-center gap-1">
          <Clock size={9} />
          {ageS != null ? `${ageS}s ago` : "—"}
        </span>
        {stale && (
          <span
            data-testid={`${tid}-stale-warning`}
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em] inline-flex items-center gap-1"
          >
            <AlertTriangle size={9} />
            Snapshot stale — refresh before relying on this scan.
          </span>
        )}
        {snapshot.rate_limited && (
          <span
            data-testid={`${tid}-rate-limited`}
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
          >
            RATE_LIMITED
          </span>
        )}
        {snapshot.partial && (
          <span
            data-testid={`${tid}-partial`}
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
          >
            PARTIAL
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap text-[#8A8A93]">
        <span className="uppercase tracking-[0.12em]">Symbol</span>
        <select
          data-testid={`${tid}-symbol`}
          value={selected}
          onChange={e => setChosenSymbol(e.target.value)}
          className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0]"
        >
          {symbols.length === 0 && <option value="">—</option>}
          {symbols.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {row && (
          <>
            <span data-testid={`${tid}-source`} className="text-[#5F5F66]">
              SRC <span className="text-[#E0E0E0]">{row.source}</span>
            </span>
            {row.chosen_expiration && (
              <span data-testid={`${tid}-expiration`} className="text-[#5F5F66]">
                EXP <span className="text-[#E0E0E0]">{row.chosen_expiration}</span>
              </span>
            )}
            <span data-testid={`${tid}-chain-rows`} className="text-[#5F5F66]">
              CHAIN <span className="text-[#E0E0E0]">{(row.chain || []).length}</span>
            </span>
            <span data-testid={`${tid}-puts`} className="text-[#5F5F66]">
              PUTS <span className="text-[#E0E0E0]">{(row.chain || []).filter(r => r.type === "put").length}</span>
            </span>
            <span data-testid={`${tid}-calls`} className="text-[#5F5F66]">
              CALLS <span className="text-[#E0E0E0]">{(row.chain || []).filter(r => r.type === "call").length}</span>
            </span>
          </>
        )}
        <button
          type="button"
          data-testid={`${tid}-apply`}
          onClick={apply}
          disabled={!row}
          className="px-2 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20 uppercase tracking-[0.12em] disabled:opacity-40"
        >
          Use Fresh Snapshot
        </button>
        {appliedFor && (
          <span
            data-testid={`${tid}-applied-note`}
            className="text-[#00FF88]/70 uppercase tracking-[0.12em]"
          >
            applied: {appliedFor}
          </span>
        )}
      </div>

      <div
        data-testid={`${tid}-safety-banner`}
        className="text-[9px] font-mono text-[#FFB800]/80 flex items-center gap-2 flex-wrap"
      >
        <span data-testid={`${tid}-safety-fills-only`}>Fresh snapshot fills data only.</span>
        <span>·</span>
        <span data-testid={`${tid}-safety-no-auto-run`}>Scanner does not run automatically.</span>
        <span>·</span>
        <span data-testid={`${tid}-safety-review`}>Review before running.</span>
        <span>·</span>
        <span data-testid={`${tid}-safety-no-execution`}>No trade execution.</span>
        <span>·</span>
        <span data-testid={`${tid}-safety-no-journal`}>No journal write.</span>
        <span>·</span>
        <span data-testid={`${tid}-safety-no-persistence`}>No persistence.</span>
      </div>
    </div>
  );
}
